import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Header,
  Container,
  SpaceBetween,
  FormField,
  Select,
  Textarea,
  Button,
  Badge,
  Table,
  Box,
  ColumnLayout,
  Alert,
  ProgressBar,
  ExpandableSection,
  Modal,
} from "@cloudscape-design/components";
import { listingApi, icFileApi, optimizerApi } from "./ListingCreationApi.js";
import IcFileConverter from "./IcFileConverter.jsx";
import ListingOptimizer from "./ListingOptimizer.jsx";
import ThemeToggle from "./ThemeToggle.jsx";
import { useAuth } from "../../auth/AuthProvider.jsx";
import "./listing-creation.css";

const MODE_OPTIONS = [
  { label: "Single ASIN — Generate listing for all ASINs under that brand", value: "single_asin" },
  { label: "Specific ASIN List — Generate listing for specific ASINs only", value: "specific_asin_list" },
];

const OPERATIONS = [
  { id: "amazon", label: "From Amazon", desc: "ASIN-based lookup", icon: "🛒", active: true, inDev: true },
  { id: "ic-file", label: "From IC File", desc: "Shopify / WooCommerce / Shopdeck", icon: "📁", active: true, inDev: false },
  { id: "optimizer", label: "Listing Optimizer", desc: "Optimize for D2C", icon: "✨", active: true, inDev: false },
];

export default function ListingCreation() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  // Admin-only sub-tools inside Listing Creation. Today: the "From Amazon"
  // (ASIN-based lookup) tab, while we keep IC File and Listing Optimizer
  // accessible to everyone. Non-admins see the tab in the strip but can't
  // click it, and the page lands on IC File by default for them.
  const OPERATIONS_FOR_USER = OPERATIONS.map(op =>
    op.id === "amazon" && !isAdmin ? { ...op, active: false } : op
  );
  const [activeOp, setActiveOp] = useState(isAdmin ? "amazon" : "ic-file");
  const [mode, setMode] = useState(null);
  const [asinInput, setAsinInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState("unknown");
  const [logs, setLogs] = useState("Waiting for logs...");
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackHover, setFeedbackHover] = useState(0);
  const [feedbackCategory, setFeedbackCategory] = useState(null);
  const [feedbackComment, setFeedbackComment] = useState("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");

  // Harmony username detection
  useEffect(() => {
    const waitForHarmonyUser = async () => {
      const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));
      let retries = 5;
      let login = "unknown";
      while (retries-- > 0) {
        const user = window?.harmony?.api?.getUserName?.();
        if (user) {
          login = user;
          break;
        }
        await wait(300);
      }
      setUsername(login);
    };
    waitForHarmonyUser();
  }, []);

  useEffect(() => {
    loadRecentJobs();
  }, []);

  useEffect(() => {
    if (!currentJobId) return;

    const statusInterval = setInterval(async () => {
      try {
        const status = await listingApi.getJobStatus(currentJobId);
        setJobStatus(status);

        if (status.status === "SUCCEEDED" || status.status === "FAILED") {
          clearInterval(statusInterval);
          clearInterval(logsInterval);
          loadRecentJobs();
        }
      } catch (err) {
        console.error("Error polling status:", err);
      }
    }, 2000);

    const logsInterval = setInterval(async () => {
      try {
        const data = await listingApi.getJobLogs(currentJobId);
        setLogs(data.logs || "No logs available yet...");
      } catch (err) {
        console.error("Error fetching logs:", err);
      }
    }, 2000);

    return () => {
      clearInterval(statusInterval);
      clearInterval(logsInterval);
    };
  }, [currentJobId]);

  const loadRecentJobs = async () => {
    try {
      // Fetch from all three APIs in parallel
      const [amazonData, icData, optimizerData] = await Promise.allSettled([
        listingApi.getRecentJobs(),
        icFileApi.getRecentRuns(),
        optimizerApi.getRecentRuns(),
      ]);

      const amazonJobs = (amazonData.status === "fulfilled" ? amazonData.value.jobs || [] : [])
        .map((job) => ({ ...job, _tool: "Amazon ASIN", _timestamp: job.started_at || "" }));

      const icJobs = (icData.status === "fulfilled" ? icData.value.runs || [] : [])
        .map((run) => ({ ...run, _tool: "IC File", _timestamp: run.timestamp || "" }));

      const optimizerJobs = (optimizerData.status === "fulfilled" ? optimizerData.value.runs || [] : [])
        .map((run) => ({ ...run, _tool: "Optimizer", _timestamp: run.timestamp || "" }));

      // Merge and sort by timestamp descending
      const merged = [...amazonJobs, ...icJobs, ...optimizerJobs].sort(
        (a, b) => (b._timestamp || "").localeCompare(a._timestamp || "")
      );

      // Display up to 50 merged across all three sub-tools (backends each
      // return up to 20, so the merged set is bounded at 60 anyway). 20 was
      // the original cap when the panel was new and only Listing Creation
      // existed; bumped on 23 Jun 2026 after IC File Converter runs from
      // less-recent users were getting pushed out of view by fresher entries
      // from the other two tools. See tasks.md 23 Jun entry.
      setRecentJobs(merged.slice(0, 50));
    } catch (err) {
      console.error("Error loading recent jobs:", err);
    }
  };

  const parseAsins = (input) => {
    return input
      .split(/[\n,;\s]+/)
      .map((a) => a.trim().toUpperCase())
      .filter((a) => a.length > 0);
  };

  const handleSubmit = async () => {
    setError(null);
    const asins = parseAsins(asinInput);

    if (!mode) {
      setError("Please select a mode");
      return;
    }
    if (asins.length === 0) {
      setError("Please enter at least one ASIN");
      return;
    }
    if (mode.value === "single_asin" && asins.length !== 1) {
      setError("Single ASIN mode requires exactly one ASIN");
      return;
    }

    setIsSubmitting(true);
    setJobStatus(null);

    try {
      const result = await listingApi.runJob(mode.value, asins, username);
      setCurrentJobId(result.job_id);
      setJobStatus({ status: "RUNNING", job_id: result.job_id });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = async (item) => {
    try {
      if (item._tool === "IC File") {
        await icFileApi.downloadOutput(item.run_id);
      } else if (item._tool === "Optimizer") {
        await optimizerApi.downloadOutput(item.run_id);
      } else {
        await listingApi.downloadJobOutput(item.job_id);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      RUNNING: "blue",
      SUCCEEDED: "green",
      FAILED: "red",
    };
    return <Badge color={map[status] || "grey"}>{status}</Badge>;
  };

  const feedbackCommentRequired = feedbackRating > 0 && feedbackRating <= 2;
  const canSubmitFeedback = feedbackRating > 0
    && (!feedbackCommentRequired || feedbackComment.trim().length > 0)
    && !feedbackSubmitting;

  const handleFeedbackSubmit = async () => {
    if (!canSubmitFeedback) return;
    setFeedbackSubmitting(true);
    setFeedbackError("");
    try {
      // Determine tool based on active tab
      let tool = "listing-creation";
      if (activeOp === "optimizer") tool = "listing-optimizer";
      else if (activeOp === "ic-file") tool = "listing-creation";
      else if (activeOp === "amazon") tool = "listing-creation-amazon";

      const { icFileApi } = await import("./ListingCreationApi.js");
      await icFileApi.submitFeedback({
        rating: feedbackRating,
        tool,
        category: feedbackCategory?.value || "",
        comment: feedbackComment.trim(),
        user: username,
        page: window.location?.pathname || "",
      });
      setFeedbackSubmitted(true);
      setTimeout(() => {
        setFeedbackOpen(false);
        setFeedbackRating(0);
        setFeedbackHover(0);
        setFeedbackCategory(null);
        setFeedbackComment("");
        setFeedbackSubmitted(false);
        setFeedbackError("");
      }, 1200);
    } catch (err) {
      setFeedbackError(err.message || "Could not submit feedback. Please try again.");
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // ─── Amazon Tab Content ───────────────────────────────────────────────
  const amazonContent = (
    <SpaceBetween size="l">
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Container header={<Header variant="h2">Create New Listing</Header>}>
        <SpaceBetween size="m">
          <FormField label="Mode" description="Choose how to generate the listing file">
            <Select
              selectedOption={mode}
              onChange={({ detail }) => setMode(detail.selectedOption)}
              options={MODE_OPTIONS}
              placeholder="Select mode"
            />
          </FormField>

          <FormField
            label={mode?.value === "single_asin" ? "Enter ASIN" : "Enter ASINs"}
            description={
              mode?.value === "single_asin"
                ? "Enter a single ASIN to find all products under that brand"
                : "Enter ASINs separated by commas, spaces, or new lines"
            }
          >
            <Textarea
              value={asinInput}
              onChange={({ detail }) => setAsinInput(detail.value)}
              placeholder={
                mode?.value === "single_asin"
                  ? "B0FDKSVHMM"
                  : "B0FDKSVHMM, B0F4DJB2JJ, B0F4DN1TRS"
              }
              rows={mode?.value === "single_asin" ? 1 : 4}
            />
          </FormField>

          {asinInput && (
            <Box color="text-status-info">
              {parseAsins(asinInput).length} ASIN(s) detected
            </Box>
          )}

          <Button variant="primary" loading={isSubmitting} onClick={handleSubmit} disabled={!mode || !asinInput.trim()}>
            Generate Listing File
          </Button>
        </SpaceBetween>
      </Container>

      {jobStatus && (
        <Container header={<Header variant="h2">⚙️ Current Job</Header>}>
          <SpaceBetween size="m">
            <ColumnLayout columns={4} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Status</Box>
                <div>{getStatusBadge(jobStatus.status)}</div>
              </div>
              <div>
                <Box variant="awsui-key-label">Job ID</Box>
                <Box>{jobStatus.job_id}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">ASINs</Box>
                <Box>{jobStatus.asin_count || "-"}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Rows Found</Box>
                <Box>{jobStatus.row_count ?? "-"}</Box>
              </div>
            </ColumnLayout>

            {jobStatus.status === "RUNNING" && (
              <ProgressBar status="in-progress" label="Processing..." description="Running Redshift query and Claude Vision..." />
            )}

            <ExpandableSection headerText="💻 Detailed Processing Logs" defaultExpanded={true}>
              <Box padding="s">
                <pre className="listing-logs-container">{logs}</pre>
              </Box>
            </ExpandableSection>

            {jobStatus.status === "SUCCEEDED" && jobStatus.row_count > 0 && (
              <Button variant="primary" iconName="download" onClick={() => handleDownload({ _tool: "Amazon ASIN", job_id: jobStatus.job_id })}>
                Download Listing CSV
              </Button>
            )}

            {jobStatus.status === "SUCCEEDED" && jobStatus.row_count === 0 && (
              <Alert type="warning">No results found for the given ASINs.</Alert>
            )}

            {jobStatus.status === "FAILED" && (
              <Alert type="error">{jobStatus.error || "Job failed"}</Alert>
            )}
          </SpaceBetween>
        </Container>
      )}

    </SpaceBetween>
  );

  // ─── Unified Recent Jobs (shown below all tabs) ───────────────────────
  const getUnifiedStatusBadge = (item) => {
    const status = (item.status || "").toUpperCase();
    if (status === "SUCCEEDED" || status === "COMPLETED") {
      return <span style={{ color: "#037f0c", fontWeight: 600, fontSize: "13px" }}>✓ Completed</span>;
    }
    if (status === "FAILED") {
      return <span style={{ color: "#d91515", fontWeight: 600, fontSize: "13px" }}>✗ Failed</span>;
    }
    if (status === "RUNNING" || status === "PROCESSING") {
      return <span style={{ color: "#0972d3", fontWeight: 600, fontSize: "13px" }}>⟳ Processing</span>;
    }
    return <span style={{ color: "#5f6b7a", fontSize: "13px" }}>{status || "—"}</span>;
  };

  return (
    <div className="listing-page">
      <div className="listing-container">
        <ThemeToggle />
        {/* Back link */}
        <Link
          to="/"
          className="listing-back-link"
          aria-label="Back to SmartBiz Toolkit"
        >
          ← Back to SmartBiz Toolkit
        </Link>

        {/* Hero banner with operation tabs */}
        <div className="listing-hero">
          <div className="listing-hero-left">
            <div className="listing-hero-icon">📋</div>
            <div className="listing-hero-text">
              <h1 className="listing-hero-title">Listing Creation</h1>
              <p className="listing-hero-subtitle">Create SmartBiz listing files from Amazon ASINs or your existing catalogue</p>
            </div>
          </div>
          <div className="listing-hero-ops">
            {OPERATIONS_FOR_USER.map((op) => (
              <button
                key={op.id}
                className={`listing-op-tab ${activeOp === op.id ? "active" : ""} ${!op.active ? "disabled" : ""} ${op.inDev ? "in-dev" : ""}`}
                onClick={() => op.active && setActiveOp(op.id)}
              >
                <span className="listing-op-icon">{op.icon}</span>
                <span className="listing-op-label">{op.label}</span>
                <span className="listing-op-desc">{!op.active ? "Coming Soon" : op.inDev ? "In Dev" : op.desc}</span>
              </button>
            ))}
            <button
              type="button"
              className="listing-feedback-btn"
              onClick={() => setFeedbackOpen(true)}
              title="Share feedback about Listing Creation"
              aria-label="Share feedback"
            >
              <span className="listing-feedback-icon">💬</span>
              <span className="listing-feedback-label">Feedback</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="listing-content">
          {activeOp === "amazon" && amazonContent}
          {activeOp === "ic-file" && <IcFileConverter username={username} onConversionComplete={loadRecentJobs} />}
          {activeOp === "optimizer" && (
            <ListingOptimizer username={username} onJobComplete={loadRecentJobs} />
          )}

          {/* Unified Recent Jobs — always visible */}
          <Container header={
            <Header
              variant="h2"
              actions={
                <Button iconName="refresh" onClick={loadRecentJobs}>
                  Refresh
                </Button>
              }
            >
              💼 Recent Jobs
            </Header>
          }>
            <Table
              items={recentJobs}
              variant="embedded"
              sortingDisabled
              columnDefinitions={[
                {
                  id: "tool",
                  header: "Tool",
                  cell: (item) => {
                    const icons = { "IC File": "📁", "Amazon ASIN": "🛒", "Optimizer": "✨" };
                    const icon = icons[item._tool] || "📋";
                    return <span style={{ whiteSpace: "nowrap" }}>{icon} {item._tool}</span>;
                  },
                  width: 130,
                },
                {
                  id: "details",
                  header: "Details",
                  cell: (item) => {
                    if (item._tool === "IC File") {
                      const plat = (item.platform || "—").charAt(0).toUpperCase() + (item.platform || "").slice(1);
                      const base = `${plat} · ${item.filename || "—"}`;
                      // Show "N images auto-fixed" badge when the WebP→JPEG
                      // converter rewrote any URLs for this run. Helps sellers
                      // see the value silently being delivered (and gives
                      // support a quick signal when investigating issues).
                      const stats = item.image_stats;
                      const fixed = stats
                        ? (stats.converted_webp || 0) + (stats.converted_other || 0)
                        : 0;
                      if (fixed > 0) {
                        const tooltip =
                          `${fixed} image${fixed === 1 ? "" : "s"} were stored in WebP/other formats that ` +
                          `SmartBiz can't read. We auto-converted them to JPEG and rehosted on our servers ` +
                          `so your import works without manual fixes.`;
                        return (
                          <span>
                            {base}{" "}
                            <span
                              className="webp-fixed-pill"
                              onMouseEnter={(e) => {
                                // Position the fixed-positioned tooltip just
                                // above the pill — same pattern as
                                // tu-pill-trigger to escape Cloudscape Table
                                // row overflow:hidden which would otherwise
                                // clip the tooltip behind the row above.
                                const tip = e.currentTarget.querySelector(".webp-fixed-tip");
                                if (tip) {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  tip.style.top = `${rect.top - 6}px`;
                                  tip.style.left = `${rect.left + rect.width / 2}px`;
                                }
                              }}
                            >
                              🖼 {fixed} fixed
                              <span className="webp-fixed-tip" role="tooltip">{tooltip}</span>
                            </span>
                          </span>
                        );
                      }
                      return base;
                    }
                    if (item._tool === "Optimizer") {
                      return item.filename || "—";
                    }
                    return item.mode === "single_asin" ? "Single ASIN" : `Specific List (${item.asin_count || 0})`;
                  },
                  width: 240,
                },
                {
                  id: "rows",
                  header: "Rows",
                  cell: (item) => item.row_count ?? item.total_rows ?? "-",
                  width: 70,
                },
                {
                  id: "usage",
                  header: "Usage",
                  cell: (item) => {
                    if (item._tool !== "Optimizer" && item._tool !== "IC File") return "—";
                    const total = (item.total_input_tokens || 0) + (item.total_output_tokens || 0);
                    if (!total) return "—";
                    const inputTokens = item.total_input_tokens || 0;
                    const outputTokens = item.total_output_tokens || 0;
                    const model = item._tool === "Optimizer" ? "Claude Sonnet 4.5" : "Claude Haiku";
                    const inputRate = item._tool === "Optimizer" ? 0.003 : 0.00025;
                    const outputRate = item._tool === "Optimizer" ? 0.015 : 0.00125;
                    const inputCost = (inputTokens / 1000) * inputRate;
                    const outputCost = (outputTokens / 1000) * outputRate;
                    const totalCost = item.estimated_cost_usd || (inputCost + outputCost);
                    const formatK = (n) => n >= 1000 ? `${Math.round(n / 1000)}K` : n.toLocaleString();
                    const costDisplay = totalCost >= 0.01 ? `$${totalCost.toFixed(2)}` : totalCost > 0 ? `$${totalCost.toFixed(4)}` : "$0";
                    const displayStr = `${formatK(total)} (${costDisplay})`;
                    const tooltipLines = [
                      `Model: ${model}`,
                      `Input: ${inputTokens.toLocaleString()} tokens × $${inputRate}/1K = $${inputCost.toFixed(4)}`,
                      `Output: ${outputTokens.toLocaleString()} tokens × $${outputRate}/1K = $${outputCost.toFixed(4)}`,
                      `Total: ${total.toLocaleString()} tokens`,
                      `Est. cost: ${totalCost >= 0.01 ? '$' + totalCost.toFixed(2) : totalCost > 0 ? '$' + totalCost.toFixed(4) : '$0'}`,
                      `Note: Volume discounts may apply`
                    ].join("\n");
                    return (
                      <span
                        className="listing-error-trigger"
                        style={{ cursor: "pointer", position: "relative" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const trigger = e.currentTarget;
                          const tooltip = trigger.querySelector(".listing-usage-tooltip");
                          if (!tooltip) return;
                          
                          // If already pinned, unpin and done
                          if (tooltip.classList.contains("pinned")) {
                            tooltip.classList.remove("pinned");
                            return;
                          }
                          
                          // Close any other open tooltips
                          document.querySelectorAll(".listing-usage-tooltip.pinned").forEach(el => el.classList.remove("pinned"));
                          
                          // Pin this one
                          tooltip.classList.add("pinned");
                          const rect = trigger.getBoundingClientRect();
                          tooltip.style.top = `${rect.bottom + 4}px`;
                          tooltip.style.left = `${rect.left}px`;
                          
                          // Close on outside click
                          const close = (evt) => {
                            if (!tooltip.contains(evt.target) && !trigger.contains(evt.target)) {
                              tooltip.classList.remove("pinned");
                              document.removeEventListener("mousedown", close);
                            }
                          };
                          setTimeout(() => document.addEventListener("mousedown", close), 0);
                        }}
                      >
                        {displayStr}
                        <span className="listing-usage-tooltip">{tooltipLines}</span>
                      </span>
                    );
                  },
                  width: 120,
                },
                {
                  id: "triggered",
                  header: "Triggered",
                  cell: (item) => {
                    const ts = item.started_at || item.timestamp;
                    if (!ts) return "-";
                    const d = new Date(ts);
                    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })
                      + " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
                  },
                  width: 110,
                },
                {
                  id: "duration",
                  header: "Duration",
                  cell: (item) => {
                    const start = item.started_at || item.timestamp;
                    const end = item.completed_at;
                    if (!start || !end) return "—";
                    const diffMs = new Date(end) - new Date(start);
                    const totalSec = Math.round(diffMs / 1000);
                    if (totalSec < 60) return `${totalSec}s`;
                    const mins = Math.floor(totalSec / 60);
                    const secs = totalSec % 60;
                    return `${mins}m ${secs}s`;
                  },
                  width: 90,
                },
                {
                  id: "user",
                  header: "User",
                  cell: (item) => item.user || "—",
                  width: 80,
                },
                {
                  id: "status",
                  header: "Status",
                  cell: (item) => getUnifiedStatusBadge(item),
                  width: 95,
                },
                {
                  id: "actions",
                  header: "Download",
                  cell: (item) => {
                    const status = (item.status || "").toUpperCase();
                    const rows = item.row_count ?? item.total_rows ?? 0;
                    const hasOutput = item._tool === "IC File" ? item.output_file : (item.output_file || rows > 0);

                    if ((status === "SUCCEEDED" || status === "COMPLETED") && rows > 0 && hasOutput) {
                      return (
                        <Button variant="inline-link" iconName="download" onClick={() => handleDownload(item)}>
                          CSV
                        </Button>
                      );
                    }
                    if (item.error) {
                      return (
                        <span
                          className="listing-error-trigger"
                          onMouseEnter={(e) => {
                            const tooltip = e.currentTarget.querySelector(".listing-error-tooltip");
                            if (tooltip) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              tooltip.style.top = `${rect.bottom + 4}px`;
                              tooltip.style.left = `${rect.left}px`;
                            }
                          }}
                        >
                          <span style={{ color: "#d91515", fontSize: "12px", cursor: "default" }}>⚠️ Error</span>
                          <span className="listing-error-tooltip">{item.error}</span>
                        </span>
                      );
                    }
                    return "—";
                  },
                  width: 90,
                },
              ]}
              empty={
                <Box textAlign="center" color="inherit" padding="l">
                  <SpaceBetween size="xxs">
                    <Box variant="p" color="inherit"><b>No jobs yet</b></Box>
                    <Box variant="p" color="text-body-secondary">Run a conversion to see history here.</Box>
                  </SpaceBetween>
                </Box>
              }
            />
          </Container>
        </div>
      </div>

      {/* Feedback Modal */}
      <Modal
        visible={feedbackOpen}
        onDismiss={() => { setFeedbackOpen(false); setFeedbackRating(0); setFeedbackHover(0); setFeedbackCategory(null); setFeedbackComment(""); setFeedbackSubmitted(false); setFeedbackError(""); }}
        closeAriaLabel="Close feedback"
        header="💬 Share your feedback"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setFeedbackOpen(false)} disabled={feedbackSubmitting}>Cancel</Button>
              <Button variant="primary" onClick={handleFeedbackSubmit} loading={feedbackSubmitting} disabled={!canSubmitFeedback}>
                Submit
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {feedbackSubmitted && <Alert type="success">Thanks — feedback received!</Alert>}
          {feedbackError && <Alert type="error" dismissible onDismiss={() => setFeedbackError("")}>{feedbackError}</Alert>}

          <FormField label="How would you rate your experience?" description="Required.">
            <div
              style={{ display: "inline-flex", gap: 4 }}
              onMouseLeave={() => !feedbackSubmitting && setFeedbackHover(0)}
            >
              {[1, 2, 3, 4, 5].map((n) => {
                const effective = feedbackHover || feedbackRating;
                const filled = n <= effective;
                return (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={feedbackRating === n}
                    aria-label={`${n} star${n === 1 ? "" : "s"}`}
                    onClick={() => !feedbackSubmitting && setFeedbackRating(n)}
                    onMouseEnter={() => !feedbackSubmitting && setFeedbackHover(n)}
                    onFocus={() => !feedbackSubmitting && setFeedbackHover(n)}
                    onBlur={() => !feedbackSubmitting && setFeedbackHover(0)}
                    disabled={feedbackSubmitting}
                    style={{
                      background: "none", border: "none",
                      cursor: feedbackSubmitting ? "default" : "pointer",
                      fontSize: 28, lineHeight: 1, padding: "0 2px",
                      color: filled ? "#f2c94c" : "#d1d5db",
                      transition: "color 0.12s",
                    }}
                  >
                    {filled ? "★" : "☆"}
                  </button>
                );
              })}
              {(feedbackHover || feedbackRating) > 0 && (
                <Box fontSize="body-s" color="text-body-secondary" margin={{ left: "s" }}>
                  {["", "Poor", "Fair", "OK", "Good", "Great"][feedbackHover || feedbackRating]}
                </Box>
              )}
            </div>
          </FormField>

          <FormField label="What was it about? (optional)">
            <Select
              selectedOption={feedbackCategory}
              onChange={({ detail }) => setFeedbackCategory(detail.selectedOption)}
              options={[
                { label: "Listing quality / accuracy", value: "listing_quality" },
                { label: "Missing or wrong attributes", value: "missing_attributes" },
                { label: "Too slow", value: "performance" },
                { label: "UI / workflow confusing", value: "ux" },
                { label: "File format issues", value: "file_format" },
                { label: "Other", value: "other" },
              ]}
              placeholder="Pick a category"
              disabled={feedbackSubmitting}
            />
          </FormField>

          <FormField
            label="Comments"
            description={
              feedbackCommentRequired
                ? "Required for ratings of 2 or lower — tell us what went wrong."
                : "Anything specific you want us to know? (optional)"
            }
            errorText={feedbackCommentRequired && feedbackComment.trim().length === 0 ? "Please add a short comment." : undefined}
          >
            <Textarea
              value={feedbackComment}
              onChange={({ detail }) => setFeedbackComment(detail.value)}
              placeholder={
                feedbackCommentRequired
                  ? "e.g., The generated listing had incorrect bullet points"
                  : "e.g., Liked the output, wanted more customization options"
              }
              rows={3}
              disabled={feedbackSubmitting}
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </div>
  );
}
